import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  Heart,
  LogOut,
  Mountain,
  RefreshCw,
  Save,
  Search,
  ShieldCheck,
  Sparkles,
  Trash2,
  UserRound,
  Wand2,
} from "lucide-react";
import "./styles.css";

type Workspace = "people" | "landscape";

type PromptParams = {
  workspace?: Workspace;
  subject: string;
  purpose: string;
  contentType: string;
  ratio: string;
  resolution: string;
  style: string;
  scene: string;
  safetyProfile: string;
  ageBoundary: string;
  clothing: string;
  pose: string;
  expression: string;
  promptTitle?: string;
  characterProfile?: string;
  facialFeatures?: string;
  bodyDesign?: string;
  hairDesign?: string;
  outfitDesign?: string;
  mood?: string;
  renderQuality?: string;
  aestheticDirection?: string;
  safetyRewrite?: string;
  promptEmphasis?: string;
  repairStrategy?: string;
  landscapeType?: string;
  season?: string;
  timeOfDay?: string;
  weather?: string;
  atmosphere?: string;
  background: string;
  composition: string;
  lighting: string;
  camera: string;
  colorPalette: string;
  texture: string;
  textContent: string;
  textPlacement: string;
  promptMode: string;
  details: string;
  negative: string;
  notes: string;
};

type HistoryItem = {
  id: string;
  prompt: string;
  params: PromptParams;
  tags: string;
  favorite: boolean;
  createdAt: string;
};

type HistoryResponse = {
  items: HistoryItem[];
  total: number;
};

type WorkspaceConfig = {
  key: Workspace;
  title: string;
  shortTitle: string;
  description: string;
  icon: React.ReactNode;
  defaults: PromptParams;
  templates: Array<{ name: string; patch: Partial<PromptParams> }>;
  purposes: string[];
  contentTypes: string[];
  styles: string[];
  scenes: string[];
  subjectLabel: string;
  subjectPlaceholder: string;
  detailPlaceholder: string;
};

const baseDefaults = {
  ratio: "3:4",
  resolution: "高清 1K",
  safetyProfile: "常规合规",
  textContent: "",
  textPlacement: "不需要文字",
  promptMode: "详细提示词",
  details: "",
  notes: "",
};

const peopleDefaults: PromptParams = {
  ...baseDefaults,
  workspace: "people",
  subject: "",
  purpose: "小红书配图",
  contentType: "人物/生活方式",
  style: "明亮生活方式摄影",
  scene: "生活方式场景",
  ageBoundary: "如出现人物，默认成年人，非未成年人",
  clothing: "日常得体服装，符合公开发布场景",
  pose: "自然放松的姿态，无挑逗动作",
  expression: "自然、自信、友好",
  promptTitle: "",
  characterProfile: "成年角色，气质自然成熟，整体公开发布友好",
  facialFeatures: "五官自然精致，皮肤质感真实，表情柔和",
  bodyDesign: "身体比例自然协调，体态健康优雅，不夸张不畸形",
  hairDesign: "发型自然有层次，发丝清晰，有真实光泽",
  outfitDesign: "服装完整得体，材质清晰，符合公开发布场景",
  mood: "温柔、细腻、有高级感，情绪克制",
  renderQuality: "高质量渲染，细节丰富，皮肤、发丝、布料和光影质感清晰",
  aestheticDirection: "成熟角色美感、商业写真审美、克制氛围",
  safetyRewrite: "把敏感或容易被拒的表达改写为成年人、完整得体服装、优雅姿态、非情色化、可公开发布的视觉表达",
  promptEmphasis: "面部神态、发丝层次、服装材质、光影质感、自然比例",
  repairStrategy: "如果被拒绝，请降低暧昧程度，减少身体曲线强调，强化完整服装、商业摄影、角色立绘、公开发布友好",
  background: "干净自然背景",
  composition: "主体居中，留出适合排版的空间",
  lighting: "柔和自然光",
  camera: "标准视角，真实透视",
  colorPalette: "清爽明亮，低饱和",
  texture: "细节清晰，材质真实",
  negative: "不要文字水印、不要畸形手指、不要低清晰度、不要过度磨皮、不要裸露、不要情色化表达、不要未成年人",
};

const landscapeDefaults: PromptParams = {
  ...baseDefaults,
  workspace: "landscape",
  subject: "",
  purpose: "风景摄影",
  contentType: "自然风景",
  ratio: "16:9",
  resolution: "高清 2K",
  style: "写实风光摄影",
  scene: "山川湖泊",
  safetyProfile: "常规合规",
  ageBoundary: "不涉及人物年龄",
  clothing: "不涉及服装",
  pose: "不涉及人物姿态",
  expression: "不涉及人物表情",
  landscapeType: "自然风景",
  season: "春夏清爽季节",
  timeOfDay: "清晨或傍晚黄金时刻",
  weather: "晴朗通透，少量云层",
  atmosphere: "宁静、开阔、有呼吸感",
  background: "远处有层次丰富的地平线和自然背景",
  composition: "前景、中景、远景层次清楚，保留自然留白",
  lighting: "柔和自然光，天空和地面曝光平衡",
  camera: "广角风光视角，真实透视",
  colorPalette: "自然色彩，天空、植被和地貌协调",
  texture: "岩石、水面、云层和植被细节清晰",
  negative: "不要人物、不要真实商标、不要文字水印、不要低清晰度、不要过度 HDR、不要不自然饱和",
};

const workspaces: Record<Workspace, WorkspaceConfig> = {
  people: {
    key: "people",
    title: "人物生图工作台",
    shortTitle: "人物网页",
    description: "专门处理头像、写真、穿搭、角色图，默认加入成年人、得体服装和非情色边界。",
    icon: <UserRound size={18} />,
    defaults: peopleDefaults,
    subjectLabel: "人物主体",
    subjectPlaceholder: "例如：一位成年女性咖啡师在明亮咖啡店里整理杯具，商业生活方式摄影",
    detailPlaceholder: "补充道具、服装面料、情绪、妆发、品牌色、标题留白等。",
    purposes: ["小红书配图", "头像/人物写真", "穿搭内容图", "商业人物海报", "角色设定图", "社媒封面"],
    contentTypes: ["人物/生活方式", "人物/时尚", "人物/商业肖像", "虚拟角色/插画"],
    styles: ["明亮生活方式摄影", "高级质感肖像", "时尚杂志摄影", "电影感人物视觉", "高级二次元 3D 角色渲染", "梦幻概念 3D CG 角色渲染", "精致动漫插画", "写实摄影"],
    scenes: ["生活方式场景", "人物肖像场景", "街拍或室内时尚场景", "商业棚拍场景", "角色插画场景"],
    templates: [
      {
        name: "合规人物写真",
        patch: {
          purpose: "头像/人物写真",
          contentType: "人物/商业肖像",
          ratio: "3:4",
          resolution: "高清 2K",
          style: "高级质感肖像",
          scene: "人物肖像场景",
          safetyProfile: "人物写真合规",
          ageBoundary: "明确成年人，成熟气质，非未成年人",
          clothing: "简洁得体的时装，公开场合适合的剪裁",
          pose: "自然站姿或半身坐姿，姿态优雅，不做性暗示动作",
          expression: "自信、松弛、亲和",
          background: "柔和虚化背景，干净且有高级感",
          composition: "半身或肩颈构图，面部清晰，眼神自然",
          lighting: "柔和侧光，皮肤质感自然",
          camera: "85mm 人像镜头感，真实透视",
          colorPalette: "温和自然肤色，低饱和高级色",
          characterProfile: "成年人物肖像，气质成熟自然，商业摄影审美",
          facialFeatures: "五官柔和精致，眼神自然，皮肤保留真实细节和柔和高光",
          bodyDesign: "身体比例自然协调，肩颈线条优雅，整体健康、松弛、端正",
          hairDesign: "发型干净有层次，发丝边缘清晰，带有自然光泽",
          outfitDesign: "简洁得体的时装，剪裁高级，材质真实，适合公开发布",
          mood: "自信、松弛、亲和，高级但不夸张",
          renderQuality: "真实人像摄影质感，皮肤、发丝、布料、背景虚化和光影层次细腻",
          aestheticDirection: "成熟自然的人像美感，商业肖像审美，公开发布友好",
          safetyRewrite: "把氛围表达转为成熟、自信、亲和、得体服装和非情色姿态",
          promptEmphasis: "面部神态、皮肤质感、肩颈线条、服装剪裁、背景虚化",
          repairStrategy: "如果被拒绝，请去掉暧昧词，改成商业头像、职场肖像或生活方式摄影",
        },
      },
      {
        name: "绯红之夜 3D CG",
        patch: {
          promptTitle: "绯红之夜",
          subject: "一位成年东方幻想系女性角色，躺在柔软的床铺上，头部轻轻陷在浅蓝色枕头中",
          purpose: "角色设定图",
          contentType: "虚拟角色/插画",
          ratio: "9:16",
          resolution: "高清 2K",
          style: "高级二次元 3D 角色渲染",
          scene: "角色插画场景",
          safetyProfile: "人物写真合规",
          ageBoundary: "明确成年女性角色，成熟幻想系气质，非未成年人",
          clothing: "黑色缎面质感的高级居家吊带连衣裙或露肩家居连衣裙，设计完整得体，领口优雅克制",
          pose: "躺在柔软床铺上的上半身近景姿态，动作轻柔自然，不做夸张挑逗动作",
          expression: "柔和、安静、略带羞涩和慵懒气质",
          background: "柔软床铺、浅蓝色枕头与简洁卧室环境，背景虚化，不加入复杂杂物",
          composition: "9:16 竖版构图，镜头从上方微俯视拍摄，上半身近景人像，人物位于画面中央",
          lighting: "柔和室内光，冷蓝与暖白混合照明，突出皮肤通透感、红发光泽和黑色缎面服装质感",
          camera: "上方微俯视镜头，上半身近景，真实透视，画面重点集中在面部、发丝、肩颈和服装材质",
          colorPalette: "绯红发色、浅蓝枕头、黑色缎面、冷蓝与暖白光线形成高级对比",
          texture: "高质量 3D CG 发丝、皮肤、缎面布料、蕾丝边缘、枕头和床铺材质都细腻清晰",
          characterProfile: "成年东方幻想系女性角色，高级二次元 3D 角色渲染，整体成熟、温柔、梦幻，公开发布友好",
          facialFeatures: "精致柔和的东方幻想系五官，白皙通透肌肤，肤质细腻柔滑，脸颊淡淡红晕，粉紫色瞳孔，眼神柔和湿润且安静，嘴唇自然微张",
          bodyDesign: "成熟健康的女性体态，肩部圆润柔和，锁骨清晰但不夸张，身体比例自然协调，线条柔软优雅，不夸张、不畸形",
          hairDesign: "高饱和鲜艳红色长发，头顶高马尾由深色发饰固定，刘海自然垂落，部分发丝轻轻贴近额头与脸侧，长发在枕头上自然铺散，蓬松顺滑，层次丰富，边缘有细腻高光",
          outfitDesign: "黑色缎面质感的高级居家吊带睡裙或露肩家居连衣裙，带精致黑色蕾丝花边；服装贴合身体但不过度紧绷，布料柔软自然，有丝滑反光、轻微褶皱与真实织物质感",
          mood: "温柔、暧昧但克制，高级、细腻、梦幻，更像高级角色立绘与精致居家时尚写真",
          renderQuality: "高质量 3D CG 动漫角色宣传图质感，皮肤通透、发丝顺滑、缎面反光自然、背景虚化干净，细节精致",
          negative: "不要裸露、不要透视服装、不要情色化表达、不要未成年人、不要夸张挑逗动作、不要低清晰度、不要畸形手指、不要文字水印",
          aestheticDirection: "成年幻想系角色美感，高级二次元 3D 宣传图，温柔克制的居家时尚氛围",
          safetyRewrite: "把暧昧感控制为成熟、温柔、梦幻、非情色的角色宣传图表达；强调完整得体服装和自然姿态",
          promptEmphasis: "精致面容、鲜艳红发、柔和眼神、肩颈线条、缎面服装、卧室柔光、3D CG 质感",
          repairStrategy: "如果被拒绝，请删除暧昧，改成高级角色立绘、居家时尚写真、完整服装、半身近景",
        },
      },
      {
        name: "蓝梦初醒",
        patch: {
          promptTitle: "蓝梦初醒",
          subject: "一位成年幻想系女性角色在清晨蓝色卧室中醒来，坐在床边整理披肩",
          purpose: "角色设定图",
          contentType: "虚拟角色/插画",
          ratio: "9:16",
          resolution: "高清 2K",
          style: "高级二次元 3D 角色渲染",
          scene: "角色插画场景",
          safetyProfile: "人物写真合规",
          ageBoundary: "明确成年女性角色，成熟温柔气质，非未成年人",
          clothing: "浅蓝色针织披肩与完整居家连衣裙，柔软得体，不透视不暴露",
          pose: "坐在床边，手部自然整理披肩，动作轻柔自然",
          expression: "清醒、安静、略带睡意但克制",
          characterProfile: "成年东方幻想系角色，清晨醒来时的温柔状态，适合公开发布的角色宣传图",
          facialFeatures: "五官柔和，眼神清澈，脸颊自然红润，皮肤有清晨柔光",
          bodyDesign: "体态自然健康，坐姿端正放松，比例协调",
          hairDesign: "蓝黑色长发自然披散，发丝层次清晰，边缘有冷色高光",
          outfitDesign: "浅蓝针织披肩、完整居家连衣裙、柔软布料褶皱和织物纹理清楚",
          mood: "清晨、安静、梦醒、温柔、克制",
          background: "浅蓝色卧室、柔软床铺、白色窗帘与晨光，背景简洁虚化",
          composition: "9:16 竖版，上半身中近景，人物居中，窗光从侧面进入",
          lighting: "冷蓝晨光与柔和暖白室内光混合，皮肤和发丝有自然高光",
          camera: "微俯视中近景，真实透视，焦点在面部和上半身",
          colorPalette: "浅蓝、白色、淡金晨光、低饱和肤色",
          texture: "皮肤、发丝、针织披肩、床品和窗帘材质细腻",
          renderQuality: "高质量 3D CG 动漫角色渲染，清晨光影、织物纹理和发丝层次精致",
          aestheticDirection: "成年角色清晨梦醒美感，温柔克制，非情色化",
          safetyRewrite: "把亲密卧室氛围改写为清晨居家时尚角色图，完整得体服装，自然姿态",
          promptEmphasis: "清晨蓝光、柔和眼神、针织材质、长发层次、安静氛围",
          repairStrategy: "如果被拒绝，请强化清晨、居家时尚、完整服装，移除暧昧描述",
        },
      },
      {
        name: "坠梦琴弦",
        patch: {
          promptTitle: "坠梦琴弦",
          subject: "一位成年幻想系女性音乐角色坐在室内软椅旁，怀抱竖琴或弦乐道具",
          purpose: "角色设定图",
          contentType: "虚拟角色/插画",
          ratio: "9:16",
          resolution: "高清 2K",
          style: "梦幻概念 3D CG 角色渲染",
          scene: "角色插画场景",
          safetyProfile: "人物写真合规",
          ageBoundary: "明确成年女性角色，成熟艺术家气质，非未成年人",
          clothing: "完整优雅的幻想系长裙，肩颈设计克制，布料层次丰富",
          pose: "坐姿端正，双手自然扶住乐器，姿态安静优雅",
          expression: "沉静、专注、略带梦幻感",
          characterProfile: "成年幻想系音乐角色，像角色立绘与专辑封面结合，艺术感强",
          facialFeatures: "柔和五官，眼神沉静，皮肤有柔光和细腻阴影",
          bodyDesign: "身体比例自然，坐姿稳定，肩颈与手部结构准确",
          hairDesign: "柔顺长发垂落在肩侧，发丝与乐器边缘形成层次",
          outfitDesign: "完整幻想系长裙，丝绸、薄纱外层和金属饰件材质清晰，整体得体",
          mood: "梦幻、音乐感、沉静、高级、克制",
          background: "暗色室内舞台或梦境房间，背景有柔和光点和虚化帷幕",
          composition: "人物与乐器形成三角构图，面部和手部清晰，画面下方保留少量留白",
          lighting: "柔和舞台侧光与顶部轮廓光，突出发丝、乐器和裙摆质感",
          camera: "中景人像镜头，轻微低角度，真实透视",
          colorPalette: "深蓝、柔紫、金色点缀、低饱和肤色",
          texture: "乐器木质/金属、裙摆布料、发丝和背景光点细腻",
          renderQuality: "高质量梦幻 3D CG，乐器、手部、裙摆和光影层次精致",
          aestheticDirection: "成年角色音乐主题美感，艺术封面感，非情色化",
          safetyRewrite: "把梦幻氛围表达为音乐角色宣传图，强调完整长裙、自然坐姿和艺术感",
          promptEmphasis: "乐器、手部准确、裙摆材质、梦幻光点、沉静表情",
          repairStrategy: "如果被拒绝，请改成幻想音乐家角色立绘，减少身体描述，强化乐器和服装完整性",
        },
      },
      {
        name: "粉梦女仆风",
        patch: {
          promptTitle: "粉梦女仆风",
          subject: "一位成年女性角色穿着粉白色幻想系女仆风连衣裙，站在甜点店室内",
          purpose: "角色设定图",
          contentType: "虚拟角色/插画",
          ratio: "9:16",
          resolution: "高清 2K",
          style: "精致动漫插画",
          scene: "角色插画场景",
          safetyProfile: "敏感场景降风险",
          ageBoundary: "明确成年女性角色，成熟可爱风格，非未成年人",
          clothing: "完整粉白色女仆风连衣裙，裙长得体，围裙、蝴蝶结和蕾丝装饰完整，不透视不暴露",
          pose: "站姿自然，双手托着甜点托盘，动作端正可爱",
          expression: "甜美、亲和、自然微笑",
          characterProfile: "成年幻想系甜点店角色，甜美但成熟，适合公开发布的角色海报",
          facialFeatures: "精致五官，明亮眼神，自然微笑，皮肤柔和",
          bodyDesign: "比例自然协调，站姿端正，动作稳定",
          hairDesign: "粉棕色卷发或双马尾，发丝蓬松，配饰清晰",
          outfitDesign: "完整女仆风连衣裙，围裙、袖口、裙摆和蕾丝花边细节丰富，整体得体",
          mood: "甜美、梦幻、干净、可爱但成熟",
          background: "粉白色甜点店、蛋糕展示柜和柔和灯光，背景整洁虚化",
          composition: "人物居中，托盘作为前景点缀，画面保留标题空间",
          lighting: "柔和室内暖光，皮肤和服装边缘有干净高光",
          camera: "正面中景角色海报视角，真实透视",
          colorPalette: "粉色、白色、奶油黄、少量樱桃红点缀",
          texture: "蕾丝、棉布、甜点奶油、发丝和玻璃柜细节清晰",
          renderQuality: "高质量动漫角色海报，服装层次、甜点道具和背景光影精致",
          aestheticDirection: "成年甜美角色美感，公开发布友好，非低俗化",
          safetyRewrite: "把可爱风格明确为成年角色、完整服装、甜点店职业主题，避免未成年感和情色化",
          promptEmphasis: "完整女仆风服装、甜点道具、甜美表情、粉白色调、干净背景",
          repairStrategy: "如果被拒绝，请去掉女仆敏感联想，改成粉白甜点店服务员角色、完整制服、商业海报",
        },
      },
      {
        name: "清凉夏日",
        patch: {
          promptTitle: "清凉夏日",
          subject: "一位成年女性角色在明亮夏日阳台或海边咖啡区，手持冰饮",
          purpose: "小红书配图",
          contentType: "人物/生活方式",
          ratio: "3:4",
          resolution: "高清 2K",
          style: "明亮生活方式摄影",
          scene: "生活方式场景",
          safetyProfile: "人物写真合规",
          ageBoundary: "明确成年女性人物，成熟自然气质，非未成年人",
          clothing: "浅色夏季连衣裙或短袖衬衫搭配长裙，完整得体，清爽不暴露",
          pose: "坐在阳台或咖啡桌旁，手持冰饮，自然放松",
          expression: "清爽、轻松、自然微笑",
          characterProfile: "成年夏日生活方式人物，适合社媒封面和商业生活摄影",
          facialFeatures: "自然五官，明亮眼神，皮肤有阳光下的真实质感",
          bodyDesign: "坐姿自然，比例协调，动作轻松",
          hairDesign: "自然披发或低马尾，发丝被夏日微风轻轻带动",
          outfitDesign: "浅色夏日服装，棉麻或轻薄织物质感清楚，完整得体",
          mood: "清凉、明亮、自然、松弛、适合夏日内容",
          background: "海边咖啡区、阳台绿植或白色窗帘，背景明亮干净",
          composition: "主体居中偏一侧，冰饮作为前景，道具和留白适合排版",
          lighting: "明亮自然光，柔和阴影，避免强烈过曝",
          camera: "手机内容感，中近景，自然透视",
          colorPalette: "白色、浅蓝、柠檬黄、绿色点缀",
          texture: "冰饮水珠、织物纹理、发丝和阳光反射清晰",
          renderQuality: "清爽生活方式摄影质感，皮肤、织物、冰饮和背景层次干净",
          aestheticDirection: "成年夏日生活美感，清爽商业内容，不走暴露方向",
          safetyRewrite: "把清凉感表达为夏日服装、冰饮、自然光和生活方式摄影，而不是暴露身体",
          promptEmphasis: "冰饮、阳光、清爽服装、自然笑容、社媒留白",
          repairStrategy: "如果被拒绝，请去掉清凉敏感词，改成夏日咖啡店生活方式配图、完整服装",
        },
      },
      {
        name: "小红书人物",
        patch: {
          purpose: "小红书配图",
          contentType: "人物/生活方式",
          ratio: "3:4",
          resolution: "高清 1K",
          style: "明亮生活方式摄影",
          scene: "生活方式场景",
          safetyProfile: "常规合规",
          clothing: "日常得体服装，符合真实生活分享",
          pose: "自然抓拍感，动作轻松",
          expression: "自然、亲切、有生活感",
          background: "自然光室内背景，画面干净",
          composition: "主体突出，顶部和侧边保留标题空间",
          lighting: "明亮自然光，柔和阴影",
          camera: "手机内容感，轻微俯拍",
          colorPalette: "清爽、明亮、适合社媒",
          characterProfile: "成年生活方式人物，真实自然，适合社媒内容发布",
          facialFeatures: "五官自然，表情亲切，皮肤保留真实质感",
          bodyDesign: "自然生活状态，比例协调，动作轻松",
          hairDesign: "日常发型，蓬松自然，发丝细节清楚",
          outfitDesign: "日常得体服装，颜色干净，适合生活方式内容",
          mood: "清爽、亲切、有生活气",
          renderQuality: "手机内容感和商业摄影质感兼具，画面干净明亮",
        },
      },
      {
        name: "时尚穿搭",
        patch: {
          purpose: "穿搭内容图",
          contentType: "人物/时尚",
          ratio: "3:4",
          resolution: "高清 2K",
          style: "时尚杂志摄影",
          scene: "街拍或室内时尚场景",
          safetyProfile: "人物写真合规",
          ageBoundary: "明确成年人，成熟模特气质",
          clothing: "完整时装造型，强调面料、剪裁、层次，不强调暴露",
          pose: "自然走姿或站姿，专业时尚表达，无性暗示",
          expression: "冷静、自信、有镜头感",
          background: "简洁城市背景或高级室内背景",
          composition: "全身构图，服装轮廓完整，留出海报排版空间",
          lighting: "柔和自然光结合轻微轮廓光",
          camera: "50mm 时尚摄影视角",
          colorPalette: "高级中性色，少量亮色点缀",
          characterProfile: "成年时尚模特，专业穿搭展示，成熟自信",
          facialFeatures: "五官清晰，表情冷静自然，有镜头感",
          bodyDesign: "全身比例协调，站姿自然，体现服装轮廓与层次",
          hairDesign: "发型与穿搭风格一致，发丝整洁有层次",
          outfitDesign: "完整时装造型，强调面料、剪裁、层次和配饰，不强调暴露",
          mood: "高级、克制、时尚杂志感",
          renderQuality: "服装材质、轮廓、褶皱和环境光影细节清晰",
        },
      },
      {
        name: "动漫角色",
        patch: {
          purpose: "角色设定图",
          contentType: "虚拟角色/插画",
          ratio: "3:4",
          resolution: "高清 1K",
          style: "精致动漫插画",
          scene: "角色插画场景",
          safetyProfile: "敏感场景降风险",
          ageBoundary: "角色为成年人设定，非校园未成年表达",
          clothing: "完整角色服装，设计感强，不裸露",
          pose: "自然战斗预备姿态或站姿，无性暗示",
          expression: "坚定、有角色性格",
          background: "梦幻但不杂乱的插画背景",
          composition: "角色主体清晰，色彩和谐",
          lighting: "柔和氛围光",
          camera: "半身或全身角色构图",
          colorPalette: "明快协调的插画色彩",
          characterProfile: "成年虚拟角色设定，性格鲜明，非校园未成年表达",
          facialFeatures: "角色五官精致，眼神有故事感，表情坚定",
          bodyDesign: "角色比例自然协调，姿态有力量感，不夸张不畸形",
          hairDesign: "角色发型有辨识度，发丝层次清楚，边缘高光精致",
          outfitDesign: "完整角色服装，设计感强，材质和配件清楚，不裸露",
          mood: "梦幻、坚定、有角色设定感",
          renderQuality: "精致动漫插画质感，线条、上色、材质和氛围光完整",
        },
      },
    ],
  },
  landscape: {
    key: "landscape",
    title: "风景生图工作台",
    shortTitle: "风景网页",
    description: "专门处理自然风光、城市景观、旅行海报、壁纸背景，重点控制地貌、天气、季节和光线。",
    icon: <Mountain size={18} />,
    defaults: landscapeDefaults,
    subjectLabel: "风景主题",
    subjectPlaceholder: "例如：清晨的高山湖泊，远处雪山倒映在水面，前景有野花和湿润岩石",
    detailPlaceholder: "补充地貌、植被、水面、云层、道路、建筑、旅行氛围、画面留白等。",
    purposes: ["风景摄影", "旅行海报", "壁纸背景", "城市景观", "自然插画", "露营户外图", "文案背景图"],
    contentTypes: ["自然风景", "城市风景", "旅行海报", "户外露营", "风景插画", "背景/纹理"],
    styles: ["写实风光摄影", "电影感风景", "旅行杂志摄影", "国家地理风格", "水彩风景插画", "极简自然背景", "梦幻概念风景"],
    scenes: ["山川湖泊", "海岸沙滩", "森林溪流", "城市天际线", "沙漠公路", "乡村田野", "雪山冰川", "雨后街道"],
    templates: [
      {
        name: "山湖风光",
        patch: {
          purpose: "风景摄影",
          contentType: "自然风景",
          ratio: "16:9",
          resolution: "高清 2K",
          style: "写实风光摄影",
          scene: "山川湖泊",
          landscapeType: "高山湖泊",
          season: "春夏清爽季节",
          timeOfDay: "清晨黄金时刻",
          weather: "晴朗通透，湖面微风",
          atmosphere: "宁静、辽阔、空气清新",
          background: "远处雪山和层次丰富的天空",
          composition: "湖面倒影作为中景，前景有野花和岩石，远景雪山清晰",
          lighting: "清晨柔和侧光，云层边缘有微弱金色光线",
          camera: "24mm 广角风光视角，真实透视",
          colorPalette: "湖蓝、雪白、草绿和晨光金色自然协调",
          texture: "水面、岩石、草地和云层细节清晰",
        },
      },
      {
        name: "海边日落",
        patch: {
          purpose: "旅行海报",
          contentType: "自然风景",
          ratio: "9:16",
          resolution: "1080x1920",
          style: "电影感风景",
          scene: "海岸沙滩",
          landscapeType: "海岸线与沙滩",
          season: "夏季傍晚",
          timeOfDay: "日落蓝调时刻",
          weather: "海风轻柔，天空有层次云霞",
          atmosphere: "浪漫、安静、适合旅行封面",
          background: "远处海平线和落日余晖",
          composition: "海浪形成引导线，底部保留标题空间",
          lighting: "夕阳逆光，水面反光不过曝",
          camera: "低机位广角视角",
          colorPalette: "暖橙、海蓝、淡紫色渐变自然过渡",
          texture: "浪花、湿沙、云层和海面反光细腻",
        },
      },
      {
        name: "森林溪流",
        patch: {
          purpose: "壁纸背景",
          contentType: "自然风景",
          ratio: "16:9",
          resolution: "1920x1080",
          style: "国家地理风格",
          scene: "森林溪流",
          landscapeType: "森林与溪流",
          season: "初夏雨后",
          timeOfDay: "上午柔光",
          weather: "雨后湿润，空气通透",
          atmosphere: "清凉、治愈、自然呼吸感",
          background: "高大树木和远处雾气",
          composition: "溪流从前景流向画面深处，树木形成自然框架",
          lighting: "林间散射光，水面有细碎高光",
          camera: "低角度广角，强调纵深",
          colorPalette: "苔藓绿、溪水蓝灰、湿润棕色自然融合",
          texture: "树皮、苔藓、石头、水流和雾气细节清晰",
        },
      },
      {
        name: "城市夜景",
        patch: {
          purpose: "城市景观",
          contentType: "城市风景",
          ratio: "16:9",
          resolution: "高清 2K",
          style: "电影感风景",
          scene: "城市天际线",
          landscapeType: "城市夜景",
          season: "现代都市氛围",
          timeOfDay: "蓝调夜景时刻",
          weather: "雨后路面微湿，空气清透",
          atmosphere: "高级、安静、有商业海报感",
          background: "远处城市天际线和灯光层次",
          composition: "道路或河流作为引导线，建筑轮廓清晰",
          lighting: "城市霓虹和环境光平衡，暗部保留细节",
          camera: "中远景广角视角，真实透视",
          colorPalette: "深蓝、暖黄、少量青色霓虹点缀",
          texture: "玻璃幕墙、湿润路面、天空和灯光倒影细节清楚",
          negative: "不要真实商标、不要可识别车牌、不要人物特写、不要文字水印、不要低清晰度",
        },
      },
    ],
  },
};

const ratios = ["1:1", "3:4", "4:3", "4:5", "9:16", "16:9"];
const resolutions = ["1024x1024", "1024x1536", "1536x1024", "1080x1920", "1920x1080", "高清 1K", "高清 2K", "自定义见备注"];
const safetyProfiles = ["常规合规", "人物写真合规", "品牌/版权谨慎", "敏感场景降风险"];
const promptModes = ["详细提示词", "短提示词", "ChatGPT 对话式"];
const textPlacements = ["不需要文字", "顶部居中", "底部居中", "左侧排版", "右侧排版", "主体旁边", "按备注说明"];
const landscapeTypes = ["自然风景", "高山湖泊", "海岸线与沙滩", "森林与溪流", "城市夜景", "乡村田野", "雪山冰川", "沙漠公路"];
const seasons = ["春夏清爽季节", "盛夏明亮", "秋季金色", "冬季雪景", "雨后湿润", "自定义见备注"];
const timesOfDay = ["清晨黄金时刻", "上午柔光", "正午通透", "傍晚日落", "蓝调夜景时刻", "深夜星空"];
const weathers = ["晴朗通透，少量云层", "雨后湿润，空气通透", "薄雾朦胧", "多云柔光", "雪后清冷", "海风轻柔"];

const highRiskWords = ["未成年", "儿童", "幼女", "萝莉", "露点", "色情", "性行为", "SM", "血腥", "仇恨"];
const cautionWords = ["少女", "学生", "校服", "内衣", "泳装", "性感", "诱惑", "暴露", "裸露", "明星同款", "迪士尼", "漫威"];

function getWorkspaceFromHash(): Workspace {
  return window.location.hash === "#/landscape" ? "landscape" : "people";
}

function joinRiskText(params: PromptParams) {
  const { negative: _negative, ...riskSource } = params;
  return Object.values(riskSource)
    .join(" ")
    .replace(/非未成年人?/g, "")
    .replace(/不涉及未成年人?/g, "")
    .replace(/不是未成年人?/g, "")
    .replace(/不要未成年人?/g, "")
    .replace(/避免未成年人?/g, "")
    .replace(/非情色/g, "")
    .replace(/不要裸露/g, "")
    .replace(/避免裸露/g, "")
    .toLowerCase();
}

function isPeopleWorkspace(params: PromptParams) {
  return (params.workspace || "people") === "people";
}

function getSafetyFindings(params: PromptParams) {
  const joined = joinRiskText(params);
  const hits = highRiskWords.filter((word) => joined.includes(word.toLowerCase()));
  const cautions = cautionWords.filter((word) => joined.includes(word.toLowerCase()));
  const findings = [];

  if (hits.length) findings.push(`高风险词：${hits.join("、")}，建议改成成年人、得体服装、商业摄影、非情色表达`);
  if (cautions.length) findings.push(`谨慎词：${cautions.join("、")}，建议补充年龄边界、公开发布场景和非挑逗姿态`);
  if (isPeopleWorkspace(params)) {
    if (!params.ageBoundary.trim()) findings.push("人物场景需要明确成年人或非未成年人边界");
    if (!params.clothing.trim()) findings.push("人物场景需要明确得体服装");
    if (!params.pose.trim()) findings.push("人物场景需要明确非挑逗姿态");
  }
  if (!isPeopleWorkspace(params) && (joined.includes("人物特写") || joined.includes("美女") || joined.includes("模特"))) {
    findings.push("风景页建议避免加入人物特写，把重点留给地貌、天气、光线和空间层次");
  }
  if (params.textContent.trim() && params.textPlacement === "不需要文字") findings.push("已填写画面文字，但文字位置仍是不需要文字");
  if (params.safetyProfile === "品牌/版权谨慎" && !params.negative.includes("真实商标")) findings.push("品牌谨慎模式建议在禁用元素里排除真实商标和侵权角色");
  return findings;
}

function getPromptWarnings(params: PromptParams) {
  const warnings = [];
  if (!params.subject.trim()) warnings.push(isPeopleWorkspace(params) ? "还没有人物主体" : "还没有风景主题");
  if (!params.composition.trim()) warnings.push("缺少构图要求");
  if (!params.lighting.trim()) warnings.push("缺少光线描述");
  if (!params.background.trim()) warnings.push("缺少背景描述");
  if (!isPeopleWorkspace(params)) {
    if (!params.timeOfDay?.trim()) warnings.push("缺少时间段");
    if (!params.weather?.trim()) warnings.push("缺少天气条件");
  }
  return warnings;
}

function buildComplianceLine(params: PromptParams) {
  if (!isPeopleWorkspace(params)) {
    return [
      `合规模式：${params.safetyProfile}。`,
      "风景边界：以自然或城市景观为主体，不生成未成年人、裸露、血腥、仇恨、真实名人仿冒或侵权商标。",
      "安全要求：公开发布友好，避免可识别车牌、真实商标、侵权角色和灾难伤亡画面。",
    ].join("\n");
  }

  return [
    `合规模式：${params.safetyProfile}。`,
    `人物边界：${params.ageBoundary}；服装：${params.clothing}；姿态：${params.pose}；表情：${params.expression}。`,
    "安全要求：公开发布友好，非情色、非裸露、非血腥、非仇恨；避免真实名人仿冒、侵权商标和受版权保护角色。",
  ].join("\n");
}

function buildLandscapeLine(params: PromptParams) {
  if (isPeopleWorkspace(params)) return "";
  return [
    `风景类型：${params.landscapeType || "自然风景"}。`,
    `季节：${params.season || "自然季节"}。`,
    `时间：${params.timeOfDay || "自然光线时刻"}。`,
    `天气：${params.weather || "天气自然可信"}。`,
    `氛围：${params.atmosphere || "真实、有空间感"}。`,
  ].join("\n");
}

function buildPeopleDetailedPrompt(params: PromptParams) {
  const title = params.promptTitle?.trim();
  const opening = title
    ? `gpt image 2生成${params.style}：${title}。\n提示词：`
    : "提示词：";
  const textLine = params.textContent
    ? `画面中如需加入文字，请准确显示「${params.textContent}」，文字位置为${params.textPlacement}，文字清晰、排版自然。`
    : "画面中不需要额外文字。";

  return [
    opening,
    `生成一张${params.contentType}，主体为${params.ageBoundary || "明确成年人、非未成年人"}，整体风格为${params.style}，${params.ratio}构图，分辨率为${params.resolution}。`,
    "",
    `画面中是${params.subject || params.characterProfile || "一位成年角色"}。${params.composition}。人物位于画面视觉中心，画面重点表现角色的面部神态、发型、服装材质、身体比例、姿态与整体气质。整体氛围：${params.mood || "高级、细腻、克制、适合公开发布"}。`,
    "",
    `角色设定：${params.characterProfile || "成年角色，气质成熟自然，公开发布友好"}。${params.ageBoundary}。人物表达需要成熟、自然、得体，避免未成年人感、情色化表达或低俗暗示。`,
    "",
    `审查友好改写方向：${params.safetyRewrite || "把敏感表达改写为成年人、完整得体服装、优雅姿态、商业审美和非情色化视觉表达"}。美感方向：${params.aestheticDirection || "成熟、得体、高级、可公开发布"}。画面重点：${params.promptEmphasis || "面部神态、发丝层次、服装材质、光影质感、自然比例"}。`,
    "",
    `面部与表情：${params.facialFeatures || "五官自然精致，皮肤质感真实，表情柔和"}。表情为${params.expression}，眼神自然可信，脸部细节清晰，皮肤保留真实纹理与自然高光，不要过度磨皮。`,
    "",
    `体态与姿态：${params.bodyDesign || "身体比例自然协调，体态健康优雅，不夸张不畸形"}。姿态为${params.pose}。身体结构必须自然准确，肩颈、手部、四肢和整体比例协调，不做夸张挑逗动作。`,
    "",
    `发型与发丝：${params.hairDesign || "发型自然有层次，发丝清晰，有真实光泽"}。发丝需要蓬松顺滑，层次清楚，边缘有细腻高光，避免糊成一片或产生不自然断裂。`,
    "",
    `服装与材质：${params.outfitDesign || params.clothing}。服装需要完整得体，贴合场景与角色设定，材质、褶皱、边缘、装饰和反光都要细腻可信；避免透视、裸露、情色化或过度暴露表达。`,
    "",
    `场景与背景：${params.scene}。${params.background}。背景服务于人物，不加入复杂杂物，不抢主体；空间层次自然，适合直接作为高质量人物成图或角色宣传图。`,
    "",
    `光影与色彩：${params.lighting}。色彩为${params.colorPalette}。镜头/视角为${params.camera}。画面需要有清晰主光、柔和阴影和自然高光，突出皮肤、发丝、服装与背景的质感。`,
    "",
    `细节与渲染质量：${params.renderQuality || params.texture}。${params.texture}。${params.details ? `额外细节：${params.details}。` : ""}${textLine}`,
    "",
    buildComplianceLine(params),
    "",
    `${params.notes ? `补充说明：${params.notes}。\n` : ""}避免：${params.negative}。`,
    `失败修复策略：${params.repairStrategy || "如果被拒绝，请减少暧昧和身体曲线强调，强化完整服装、商业摄影、角色立绘、公开发布友好，并保持成年角色设定"}。`,
    "输出要求：画面精致、主体准确、人物结构自然、手指正常、服装完整、气质高级，整体可公开发布；如果某些描述可能触发安全限制，请保留审美方向，自动改写为成熟、得体、非情色、非裸露的视觉表达。",
  ].join("\n");
}

function buildPrompt(params: PromptParams) {
  if (isPeopleWorkspace(params) && params.promptMode === "详细提示词") {
    return buildPeopleDetailedPrompt(params);
  }

  const baseLines = [
    `用途：${params.purpose}。`,
    `内容类型：${params.contentType}。`,
    params.subject ? `${isPeopleWorkspace(params) ? "人物主体" : "风景主题"}：${params.subject}。` : `${isPeopleWorkspace(params) ? "人物主体" : "风景主题"}：请根据我补充的主题进行创作。`,
    `场景：${params.scene}。`,
    buildLandscapeLine(params),
    `画面比例：${params.ratio}。`,
    `分辨率：${params.resolution}。`,
    `视觉风格：${params.style}。`,
    `背景：${params.background}。`,
    `构图：${params.composition}。`,
    `光线：${params.lighting}。`,
    `镜头/视角：${params.camera}。`,
    `色彩：${params.colorPalette}。`,
    params.texture ? `材质与质感：${params.texture}。` : "",
    params.textContent ? `画面文字：请准确显示「${params.textContent}」，${params.textPlacement}。` : `画面文字：${params.textPlacement}。`,
    params.details ? `细节要求：${params.details}。` : "",
    buildComplianceLine(params),
    params.notes ? `补充说明：${params.notes}。` : "",
    params.negative ? `避免：${params.negative}。` : "",
    isPeopleWorkspace(params)
      ? "输出要求：画面清晰、主体准确、细节可信、整体适合直接用于内容发布；如果某个描述可能触发安全限制，请自动降风险改写为得体、商业、非情色的视觉表达。"
      : "输出要求：画面清晰、空间层次准确、自然细节可信、适合直接用于内容发布；如果某个描述可能触发安全限制，请自动降风险改写为安全、非侵权、非灾难伤亡的风景表达。",
  ];

  const lines = baseLines.filter(Boolean);
  if (params.promptMode === "短提示词") {
    return [
      `${params.subject || params.purpose}，${params.contentType}，${params.style}，${params.scene}，${params.ratio}，${params.resolution}`,
      isPeopleWorkspace(params)
        ? `${params.ageBoundary}，${params.clothing}，${params.pose}`
        : `${params.landscapeType}，${params.season}，${params.timeOfDay}，${params.weather}，${params.atmosphere}`,
      `${params.composition}，${params.lighting}，${params.colorPalette}`,
      "公开发布友好，非情色、非裸露、非侵权、非血腥",
      params.textContent ? `准确显示文字「${params.textContent}」，${params.textPlacement}` : "",
      params.negative ? `避免：${params.negative}` : "",
    ].filter(Boolean).join("\n");
  }

  if (params.promptMode === "ChatGPT 对话式") {
    return [
      `请帮我生成一张可公开发布、符合平台安全要求的${isPeopleWorkspace(params) ? "人物图片" : "风景图片"}，我会用于「${params.purpose}」。`,
      ...lines,
      "请优先保证主体、构图、文字、比例和分辨率准确。如果我的描述里有容易违规或被拒绝的词，请保留审美意图，改写成安全、得体、非侵权的画面。",
    ].join("\n");
  }

  return [`请生成一张高质量、可公开发布、合规友好的${isPeopleWorkspace(params) ? "人物图片" : "风景图片"}。`, ...lines].join("\n");
}

async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    credentials: "include",
    ...options,
    headers: {
      ...(options.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `请求失败：${response.status}`);
  }

  return response.json() as Promise<T>;
}

function App() {
  const [workspace, setWorkspace] = useState<Workspace>(getWorkspaceFromHash);
  const [isAuthed, setIsAuthed] = useState<boolean | null>(null);
  const [password, setPassword] = useState("");
  const [params, setParams] = useState<PromptParams>(() => workspaces[getWorkspaceFromHash()].defaults);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [search, setSearch] = useState("");
  const [ratioFilter, setRatioFilter] = useState("");
  const [styleFilter, setStyleFilter] = useState("");
  const [favoriteOnly, setFavoriteOnly] = useState(false);
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [loginError, setLoginError] = useState("");

  const config = workspaces[workspace];
  const prompt = useMemo(() => buildPrompt(params), [params]);
  const promptWarnings = useMemo(() => getPromptWarnings(params), [params]);
  const safetyFindings = useMemo(() => getSafetyFindings(params), [params]);

  const loadHistory = useCallback(async () => {
    if (!isAuthed) return;
    const query = new URLSearchParams();
    if (search) query.set("search", search);
    if (ratioFilter) query.set("ratio", ratioFilter);
    if (styleFilter) query.set("style", styleFilter);
    if (favoriteOnly) query.set("favorite", "1");
    const data = await api<HistoryResponse>(`/api/history?${query.toString()}`);
    setHistory(data.items);
  }, [favoriteOnly, isAuthed, ratioFilter, search, styleFilter]);

  useEffect(() => {
    const onHashChange = () => {
      const nextWorkspace = getWorkspaceFromHash();
      setWorkspace(nextWorkspace);
      setParams(workspaces[nextWorkspace].defaults);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    api<{ ok: boolean }>("/api/me")
      .then(() => setIsAuthed(true))
      .catch(() => setIsAuthed(false));
  }, []);

  useEffect(() => {
    void loadHistory().catch((error) => setMessage(error.message));
  }, [loadHistory]);

  function switchWorkspace(nextWorkspace: Workspace) {
    if (workspace === nextWorkspace) return;
    window.location.hash = nextWorkspace === "landscape" ? "/landscape" : "/people";
  }

  function updateField<K extends keyof PromptParams>(key: K, value: PromptParams[K]) {
    setParams((current) => ({ ...current, workspace, [key]: value }));
  }

  async function login(event: React.FormEvent) {
    event.preventDefault();
    setLoginError("");
    try {
      await api<{ ok: boolean }>("/api/login", {
        method: "POST",
        body: JSON.stringify({ password }),
      });
      setPassword("");
      setIsAuthed(true);
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : "登录失败");
    }
  }

  async function logout() {
    await api<{ ok: boolean }>("/api/logout", { method: "POST", body: "{}" }).catch(() => undefined);
    setIsAuthed(false);
  }

  async function copyPrompt() {
    await navigator.clipboard.writeText(prompt);
    setMessage("提示词已复制");
  }

  async function saveCurrentPrompt() {
    setIsSaving(true);
    setMessage("");

    try {
      await api<{ ok: boolean; id: string }>("/api/prompts", {
        method: "POST",
        body: JSON.stringify({
          prompt,
          params: { ...params, workspace },
          tags: [config.shortTitle, params.purpose, params.contentType, params.ratio, params.resolution, params.style, params.safetyProfile].join(","),
        }),
      });
      setMessage("提示词已保存到历史记录");
      await loadHistory();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "保存失败");
    } finally {
      setIsSaving(false);
    }
  }

  async function toggleFavorite(item: HistoryItem) {
    await api<{ ok: boolean }>(`/api/history/${item.id}`, {
      method: "PATCH",
      body: JSON.stringify({ favorite: !item.favorite }),
    });
    setHistory((items) => items.map((entry) => (entry.id === item.id ? { ...entry, favorite: !entry.favorite } : entry)));
  }

  async function removeItem(item: HistoryItem) {
    await api<{ ok: boolean }>(`/api/history/${item.id}`, { method: "DELETE" });
    setHistory((items) => items.filter((entry) => entry.id !== item.id));
  }

  function reuseItem(item: HistoryItem) {
    const nextWorkspace = item.params.workspace || (item.params.contentType?.includes("风景") || item.params.contentType?.includes("城市") ? "landscape" : "people");
    const defaults = workspaces[nextWorkspace].defaults;
    setWorkspace(nextWorkspace);
    setParams({ ...defaults, ...item.params, workspace: nextWorkspace });
    window.location.hash = nextWorkspace === "landscape" ? "/landscape" : "/people";
    setMessage("已复用该提示词参数");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  if (isAuthed === null) {
    return <div className="boot">加载中</div>;
  }

  if (!isAuthed) {
    return (
      <main className="login-page">
        <form className="login-panel" onSubmit={login}>
          <div className="brand-mark">
            <ShieldCheck size={24} />
          </div>
          <h1>合规生图提示词工作台</h1>
          <p>私用网页，不调用 OpenAI API，只生成可复制到 ChatGPT 的安全提示词。</p>
          <label>
            私用密码
            <input
              autoFocus
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="输入访问密码"
            />
          </label>
          {loginError && <p className="error">{loginError}</p>}
          <button className="primary-button" type="submit">登录</button>
        </form>
      </main>
    );
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <div>
          <h1>{config.title}</h1>
          <p>{config.description}</p>
        </div>
        <button className="icon-button text-button" onClick={logout} title="退出登录">
          <LogOut size={18} />
          退出
        </button>
      </header>

      <nav className="workspace-switch" aria-label="工作台切换">
        {(Object.keys(workspaces) as Workspace[]).map((item) => (
          <button
            key={item}
            className={`workspace-tab ${workspace === item ? "active" : ""}`}
            onClick={() => switchWorkspace(item)}
            type="button"
          >
            {workspaces[item].icon}
            <span>{workspaces[item].shortTitle}</span>
          </button>
        ))}
      </nav>

      <section className="workspace-grid">
        <div className="tool-panel prompt-form">
          <div className="panel-title">
            <Wand2 size={18} />
            <h2>{workspace === "people" ? "人物向导" : "风景向导"}</h2>
          </div>

          <h3 className="form-section-title">{workspace === "people" ? "人物模板" : "风景模板"}</h3>
          <div className="template-row">
            {config.templates.map((template) => (
              <button
                key={template.name}
                type="button"
                className="template-chip"
                onClick={() => setParams((current) => ({ ...current, workspace, ...template.patch }))}
              >
                {template.name}
              </button>
            ))}
          </div>

          <h3 className="form-section-title">生成意图</h3>
          <label className="field wide">
            {config.subjectLabel}
            <textarea
              value={params.subject}
              onChange={(event) => updateField("subject", event.target.value)}
              placeholder={config.subjectPlaceholder}
            />
          </label>

          <div className="field-grid">
            <label className="field">
              用途
              <select value={params.purpose} onChange={(event) => updateField("purpose", event.target.value)}>
                {config.purposes.map((item) => <option key={item}>{item}</option>)}
              </select>
            </label>
            <label className="field">
              内容类型
              <select value={params.contentType} onChange={(event) => updateField("contentType", event.target.value)}>
                {config.contentTypes.map((item) => <option key={item}>{item}</option>)}
              </select>
            </label>
            <label className="field">
              比例
              <select value={params.ratio} onChange={(event) => updateField("ratio", event.target.value)}>
                {ratios.map((item) => <option key={item}>{item}</option>)}
              </select>
            </label>
            <label className="field">
              分辨率
              <select value={params.resolution} onChange={(event) => updateField("resolution", event.target.value)}>
                {resolutions.map((item) => <option key={item}>{item}</option>)}
              </select>
            </label>
            <label className="field">
              风格
              <select value={params.style} onChange={(event) => updateField("style", event.target.value)}>
                {config.styles.map((item) => <option key={item}>{item}</option>)}
              </select>
            </label>
            <label className="field">
              场景
              <select value={params.scene} onChange={(event) => updateField("scene", event.target.value)}>
                {config.scenes.map((item) => <option key={item}>{item}</option>)}
              </select>
            </label>
          </div>

          {workspace === "people" ? (
            <>
              <h3 className="form-section-title">详细提示词素材</h3>
              <div className="field-grid">
                <label className="field">
                  标题
                  <input value={params.promptTitle || ""} onChange={(event) => updateField("promptTitle", event.target.value)} placeholder="例如：绯红之夜" />
                </label>
                <label className="field">
                  氛围
                  <input value={params.mood || ""} onChange={(event) => updateField("mood", event.target.value)} />
                </label>
              </div>

              <label className="field wide">
                角色设定
                <textarea value={params.characterProfile || ""} onChange={(event) => updateField("characterProfile", event.target.value)} />
              </label>

              <div className="field-grid">
                <label className="field">
                  面部与表情
                  <textarea value={params.facialFeatures || ""} onChange={(event) => updateField("facialFeatures", event.target.value)} />
                </label>
                <label className="field">
                  体态与姿态
                  <textarea value={params.bodyDesign || ""} onChange={(event) => updateField("bodyDesign", event.target.value)} />
                </label>
                <label className="field">
                  发型与发丝
                  <textarea value={params.hairDesign || ""} onChange={(event) => updateField("hairDesign", event.target.value)} />
                </label>
                <label className="field">
                  服装与材质
                  <textarea value={params.outfitDesign || ""} onChange={(event) => updateField("outfitDesign", event.target.value)} />
                </label>
              </div>

              <label className="field wide">
                渲染质量
                <textarea value={params.renderQuality || ""} onChange={(event) => updateField("renderQuality", event.target.value)} />
              </label>

              <h3 className="form-section-title">审查友好改写</h3>
              <div className="field-grid">
                <label className="field">
                  美感方向
                  <textarea value={params.aestheticDirection || ""} onChange={(event) => updateField("aestheticDirection", event.target.value)} />
                </label>
                <label className="field">
                  安全改写
                  <textarea value={params.safetyRewrite || ""} onChange={(event) => updateField("safetyRewrite", event.target.value)} />
                </label>
                <label className="field">
                  画面重点
                  <textarea value={params.promptEmphasis || ""} onChange={(event) => updateField("promptEmphasis", event.target.value)} />
                </label>
                <label className="field">
                  失败修复
                  <textarea value={params.repairStrategy || ""} onChange={(event) => updateField("repairStrategy", event.target.value)} />
                </label>
              </div>

              <h3 className="form-section-title">人物合规边界</h3>
              <div className="field-grid">
                <label className="field">
                  安全模式
                  <select value={params.safetyProfile} onChange={(event) => updateField("safetyProfile", event.target.value)}>
                    {safetyProfiles.map((item) => <option key={item}>{item}</option>)}
                  </select>
                </label>
                <label className="field">
                  年龄边界
                  <input value={params.ageBoundary} onChange={(event) => updateField("ageBoundary", event.target.value)} />
                </label>
                <label className="field">
                  服装边界
                  <input value={params.clothing} onChange={(event) => updateField("clothing", event.target.value)} />
                </label>
                <label className="field">
                  姿态边界
                  <input value={params.pose} onChange={(event) => updateField("pose", event.target.value)} />
                </label>
                <label className="field">
                  表情气质
                  <input value={params.expression} onChange={(event) => updateField("expression", event.target.value)} />
                </label>
                <label className="field">
                  禁用元素
                  <input value={params.negative} onChange={(event) => updateField("negative", event.target.value)} />
                </label>
              </div>
            </>
          ) : (
            <>
              <h3 className="form-section-title">风景参数</h3>
              <div className="field-grid">
                <label className="field">
                  风景类型
                  <select value={params.landscapeType} onChange={(event) => updateField("landscapeType", event.target.value)}>
                    {landscapeTypes.map((item) => <option key={item}>{item}</option>)}
                  </select>
                </label>
                <label className="field">
                  季节
                  <select value={params.season} onChange={(event) => updateField("season", event.target.value)}>
                    {seasons.map((item) => <option key={item}>{item}</option>)}
                  </select>
                </label>
                <label className="field">
                  时间
                  <select value={params.timeOfDay} onChange={(event) => updateField("timeOfDay", event.target.value)}>
                    {timesOfDay.map((item) => <option key={item}>{item}</option>)}
                  </select>
                </label>
                <label className="field">
                  天气
                  <select value={params.weather} onChange={(event) => updateField("weather", event.target.value)}>
                    {weathers.map((item) => <option key={item}>{item}</option>)}
                  </select>
                </label>
                <label className="field wide-field">
                  氛围
                  <input value={params.atmosphere} onChange={(event) => updateField("atmosphere", event.target.value)} />
                </label>
                <label className="field">
                  禁用元素
                  <input value={params.negative} onChange={(event) => updateField("negative", event.target.value)} />
                </label>
              </div>
            </>
          )}

          <h3 className="form-section-title">视觉参数</h3>
          <label className="field wide">
            构图
            <input value={params.composition} onChange={(event) => updateField("composition", event.target.value)} />
          </label>

          <div className="field-grid">
            <label className="field">
              背景
              <input value={params.background} onChange={(event) => updateField("background", event.target.value)} />
            </label>
            <label className="field">
              光线
              <input value={params.lighting} onChange={(event) => updateField("lighting", event.target.value)} />
            </label>
            <label className="field">
              镜头/视角
              <input value={params.camera} onChange={(event) => updateField("camera", event.target.value)} />
            </label>
            <label className="field">
              色彩
              <input value={params.colorPalette} onChange={(event) => updateField("colorPalette", event.target.value)} />
            </label>
            <label className="field">
              材质
              <input value={params.texture} onChange={(event) => updateField("texture", event.target.value)} />
            </label>
            <label className="field">
              复制模式
              <select value={params.promptMode} onChange={(event) => updateField("promptMode", event.target.value)}>
                {promptModes.map((item) => <option key={item}>{item}</option>)}
              </select>
            </label>
          </div>

          <h3 className="form-section-title">文字与备注</h3>
          <div className="field-grid">
            <label className="field">
              画面文字
              <input
                value={params.textContent}
                onChange={(event) => updateField("textContent", event.target.value)}
                placeholder={workspace === "people" ? "例如：NEW ARRIVAL" : "例如：MOUNTAIN ESCAPE"}
              />
            </label>
            <label className="field">
              文字位置
              <select value={params.textPlacement} onChange={(event) => updateField("textPlacement", event.target.value)}>
                {textPlacements.map((item) => <option key={item}>{item}</option>)}
              </select>
            </label>
          </div>

          <label className="field wide">
            细节
            <textarea value={params.details} onChange={(event) => updateField("details", event.target.value)} placeholder={config.detailPlaceholder} />
          </label>

          <label className="field wide">
            备注
            <input value={params.notes} onChange={(event) => updateField("notes", event.target.value)} placeholder="例如：自定义分辨率、参考平台、发布渠道" />
          </label>
        </div>

        <div className="side-stack">
          <div className="tool-panel output-panel">
            <div className="panel-title">
              <Copy size={18} />
              <h2>{workspace === "people" ? "人物提示词" : "风景提示词"}</h2>
            </div>
            <textarea className="prompt-output" value={prompt} readOnly />
            <div className={`quality-box ${promptWarnings.length ? "warn" : "ok"}`}>
              <Sparkles size={17} />
              <span>{promptWarnings.length ? `可优化：${promptWarnings.join("、")}` : "画面参数完整，可以复制使用"}</span>
            </div>
            <div className={`quality-box ${safetyFindings.length ? "danger" : "ok"}`}>
              {safetyFindings.length ? <AlertTriangle size={17} /> : <CheckCircle2 size={17} />}
              <span>{safetyFindings.length ? `合规检查：${safetyFindings.join("；")}` : "合规检查通过：已包含公开发布、安全边界和降风险要求"}</span>
            </div>
            <button className="primary-button" onClick={copyPrompt}>
              <Copy size={18} />
              复制提示词
            </button>
          </div>

          <div className="tool-panel save-panel">
            <div className="panel-title">
              <Save size={18} />
              <h2>历史保存</h2>
            </div>
            <p>保存当前{workspace === "people" ? "人物" : "风景"}提示词、分辨率、风格和合规参数，之后可以搜索、收藏、复用。</p>
            <button className="secondary-button" onClick={saveCurrentPrompt} disabled={isSaving}>
              <Save size={18} />
              {isSaving ? "保存中" : "保存提示词"}
            </button>
          </div>
        </div>
      </section>

      {message && <div className="toast">{message}</div>}

      <section className="history-section">
        <div className="history-head">
          <div>
            <h2>历史记录</h2>
            <p>{history.length} 条提示词</p>
          </div>
          <div className="history-actions">
            <label className="search-field">
              <Search size={17} />
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索提示词" />
            </label>
            <select value={ratioFilter} onChange={(event) => setRatioFilter(event.target.value)} title="比例筛选">
              <option value="">全部比例</option>
              {ratios.map((item) => <option key={item}>{item}</option>)}
            </select>
            <select value={styleFilter} onChange={(event) => setStyleFilter(event.target.value)} title="风格筛选">
              <option value="">全部风格</option>
              {[...new Set([...workspaces.people.styles, ...workspaces.landscape.styles])].map((item) => <option key={item}>{item}</option>)}
            </select>
            <button
              className={`icon-button ${favoriteOnly ? "active" : ""}`}
              onClick={() => setFavoriteOnly((value) => !value)}
              title="收藏筛选"
            >
              <Heart size={18} />
            </button>
            <button className="icon-button" onClick={() => void loadHistory()} title="刷新">
              <RefreshCw size={18} />
            </button>
          </div>
        </div>

        <div className="prompt-history-grid">
          {history.map((item) => {
            const itemWorkspace = item.params.workspace || (item.params.contentType?.includes("风景") || item.params.contentType?.includes("城市") ? "landscape" : "people");
            return (
              <article className="prompt-card" key={item.id}>
                <div className="prompt-card-head">
                  <strong>{workspaces[itemWorkspace].shortTitle} · {item.params.purpose}</strong>
                  <span>
                    {item.params.ratio} / {item.params.resolution || "未设分辨率"} / {item.params.safetyProfile || "常规合规"}
                  </span>
                </div>
                <p>{item.prompt}</p>
                <div className="card-actions">
                  <button className="icon-button" onClick={() => reuseItem(item)} title="复用提示词">
                    <Copy size={17} />
                  </button>
                  <button className={`icon-button ${item.favorite ? "active" : ""}`} onClick={() => toggleFavorite(item)} title="收藏">
                    <Heart size={17} />
                  </button>
                  <button className="icon-button danger" onClick={() => removeItem(item)} title="删除">
                    <Trash2 size={17} />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </main>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
